import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconButton, styled, Tooltip, Typography } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, TablePagination, Box, Checkbox, TextField, MenuItem,
  FormControlLabel,
  FormGroup,
  Grid,
  alpha
} from '@mui/material';
import { AssociationConfirmUserModel, ConfirmUser, EApprovedFromAssociation, UserInfosModel } from '../../../models/user/UserInfosModel';
import { useTranslationContext } from '../../../hooks/translations/translationsProvider';
import { BLACK, BLUE_LIGHT_RGB_BORDER, GRAY_LIGHT_RGB_BORDER, GREEN_LOGO, GREEN_MAIN, WHITE } from '../../../theme/colors';
import { associationUsersPrivateList, postAssociationConfirmUser, usersPrivateList } from '../../../hooks/actions/admin';
import { delay } from '../../../utilities/delay';
import CustomWaiter from '../../Waiter';
import Search from '../../Search';
import { theme } from '../../../theme';
import { CustomTableContainer } from '../style';

interface TableCellCustomProps {
  user: UserInfosModel;
}
const TableCellCustom = styled(TableCell)<TableCellCustomProps>(({theme, user}) => ({
  color: user.enabledUser && user.approvedFromAssociation === EApprovedFromAssociation.ApprovedFromAssociation ? GREEN_MAIN : 
          !user.enabledUser && user.approvedFromAssociation === EApprovedFromAssociation.FirstRequest ? 'red' :
          user.enabledUser && user.approvedFromAssociation === EApprovedFromAssociation.FirstRequest ? 'red' :
          !user.enabledUser && user.approvedFromAssociation !== EApprovedFromAssociation.FirstRequest ? `${GREEN_LOGO}75` : GREEN_MAIN,
  whiteSpace: 'nowrap'
}));

interface Props {
  onUserSelect: (user: UserInfosModel) => void;
}

const AssociationPrivateUsersTable: React.FC<Props> = ({
  onUserSelect,
}) => {
  const [users, setUsers] = useState<UserInfosModel[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [enabledFilter, setEnabledFilter] = useState<boolean | null>(null);
  const [approvalFilter, setApprovalFilter] = useState<EApprovedFromAssociation | null>(null); // inserire l'eventuale valore di default
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [searchWaiter, setSearchWaiter] = useState<boolean>(false);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const {t} = useTranslationContext();
  const minChars = 2;

  const fetchUsers = () => {
    associationUsersPrivateList(page, rowsPerPage, searchTerm, approvalFilter, enabledFilter)
      .then(response => {
        
        setUsers(response.users);
        setTotalRecords(response.totalRecords);
      })
      .catch(e => console.error(e));

    delay(500).then(()=>{
      setSearchWaiter(false);
    })
  };

  useEffect(() => {
    fetchUsers();
  },[]); // ricerca all'avvio

   useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    setSearchWaiter(true);
    debounceTimeout.current = setTimeout(() => {
      fetchUsers();
      debounceTimeout.current = null;
    }, 1200);
  }, 
  [page, rowsPerPage, enabledFilter, approvalFilter, searchTerm]);

  const handleInput = (newString: string) => {
    if (newString.length < minChars) {
      setSearchTerm(null);
      return;
    }
    if (newString === searchTerm) return;
    if (newString.length > 20) return;
    setSearchTerm(newString);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (value: boolean | null) => {
    setEnabledFilter((prev) => (prev === value ? null : value));
  };

  const handleConfirmUser = useCallback(async(
      user: UserInfosModel,
      confirm: ConfirmUser,
    ) => {
      try {
        const model: AssociationConfirmUserModel = {
          associationId: user.associationId || "",
          userId: user.userId,
          confirmUser: confirm,
          userInfosModel: user,
        };
    
        // Effettua la richiesta POST
        const response = await postAssociationConfirmUser(model);
            
        // Aggiorna lo stato della lista utenti se necessario // lo farei con la risposta
        if(confirm === ConfirmUser.Confirm){
          setUsers(prevUsers =>
            prevUsers.map(u =>
              u.userId === user.userId
                ? { ...u, approvedFromAssociation: response.approvedFromAssociation, enabledUser: response.enabledUser }
                : u
            )
          );
        }else if(confirm === ConfirmUser.Reject){
          setUsers(prevUsers =>
            prevUsers.filter(u => u.userId !== user.userId) // Filtra gli utenti rimuovendo quello con lo stesso userId          
          );
        }else{}
        
      } catch (error) {
        console.error("Errore nella conferma utente", error);
      }
    }, [setUsers]);


  return (
    <Box sx={{}}>
      <Paper sx={{ 
        borderColor: 'transparent',  
        overflow: 'hidden',
        maxWidth: '100%', // Limita la larghezza massima del Paper
        boxSizing: 'border-box',
        boxShadow: 'none',
        paddingLeft: 5,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems:'center' }}>
          <Grid container spacing={2} justifyContent="left" paddingBottom={2} marginTop='0px'>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label={t("Status")}
                value={enabledFilter === true ? "enabled" : enabledFilter === false ? "disabled" : ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "enabled") {
                    handleFilterChange(true);
                  } else if (value === "disabled") {
                    handleFilterChange(false);
                  } else {
                    handleFilterChange(null);
                  }
                }}
                variant="outlined"
                sx={{ 
                  width: '100%',
                  height:'35px',
                  backgroundColor: `${GREEN_LOGO}10`,
                  '&:hover': {
                    backgroundColor: `${GREEN_LOGO}20`,
                  },
                  '& .MuiInputLabel-root': {
                    marginTop: '-6px',
                    color:GREEN_MAIN,
                    fontSize: '1rem'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius:'5px',
                    border:`1px solid ${GREEN_LOGO}20`
                  },
                  '& .MuiSelect-select': {
                    padding: '12px 12px',
                    lineHeight: 1.4,
                    color: "grey",
                    fontSize: '1rem',
                    textTransform: 'none'
                  },
                  '& .MuiMenuItem-root': {
                    fontSize: '1rem',
                    color: GREEN_MAIN,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 128, 0, 0.08)'
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(0, 128, 0, 0.12)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 128, 0, 0.16)'
                      }
                    }
                  }
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        '& .MuiMenuItem-root': {
                          fontSize: '1rem',
                          color: GREEN_MAIN,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 128, 0, 0.08)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(0, 128, 0, 0.12)',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 128, 0, 0.16)'
                            }
                          }
                        }
                      }
                    }
                  }
                }}
                InputProps={{
                  style: {
                    height: '35px',
                  },
                }}
              >
                <MenuItem value="">
                  <em>{t("Tutti")}</em>
                </MenuItem>
                <MenuItem value="enabled">{t("Utenti Abilitati")}</MenuItem>
                <MenuItem value="disabled">{t("Utenti Disabilitati")}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label={t("Operatività")}
                value={approvalFilter ?? ''} 
                onChange={(e) => setApprovalFilter(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                variant="outlined"
                sx={{ 
                  width: '100%',
                  height:'35px',
                  backgroundColor: `${GREEN_LOGO}10`,
                  '&:hover': {
                    backgroundColor: `${GREEN_LOGO}20`,
                  },
                  '& .MuiInputLabel-root': {
                    marginTop: '-6px',
                    color:GREEN_MAIN,
                    fontSize: '1rem'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius:'5px',
                    border:`1px solid ${GREEN_LOGO}20`
                  },
                  '& .MuiSelect-select': {
                    padding: '12px 12px',
                    lineHeight: 1.4,
                    color: "grey",
                    fontSize: '1rem',
                    textTransform: 'none'
                  },
                  '& .MuiMenuItem-root': {
                    fontSize: '1rem',
                    color: GREEN_MAIN,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 128, 0, 0.08)'
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(0, 128, 0, 0.12)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 128, 0, 0.16)'
                      }
                    }
                  }
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        '& .MuiMenuItem-root': {
                          fontSize: '1rem',
                          color: GREEN_MAIN,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 128, 0, 0.08)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(0, 128, 0, 0.12)',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 128, 0, 0.16)'
                            }
                          }
                        }
                      }
                    }
                  }
                }}
                InputProps={{
                  style: {
                    height: '35px',
                  },
                }}
              >
                <MenuItem value="">
                  <em>{t("Tutti gli status")}</em>
                </MenuItem>
                {Object.keys(EApprovedFromAssociation).map((key) => {
                  const typedKey = key as keyof typeof EApprovedFromAssociation;

                  if (isNaN(Number(typedKey))) {
                    const value = EApprovedFromAssociation[typedKey];
                    const displayValue = value === 0 ? 0 : value;
                    
                    return (
                      <MenuItem key={typedKey} value={displayValue}>
                        {typedKey === 'FirstRequest' ? t('FirstRequest') : t(typedKey)}
                      </MenuItem>
                    );
                  }
                  return null;
                })}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} display={'flex'}>
              <Search 
                  handleInput={handleInput}
                  search={searchTerm!!}
                  setSearch={setSearchTerm}
              />
              
              <div style={{
                  paddingTop:'3px',
                  paddingLeft:'3px',
                  width:'50px',
                  height:'35px'
              }}>
                  {searchWaiter && <CustomWaiter size={30} width={40}/>}
              </div>
            </Grid>
          </Grid>
        </Box>

        <CustomTableContainer sx={{}}>
          <Table sx={{}}>
            <TableHead>
              <TableRow>
                {/*<TableCell sx={{
                    position: 'sticky',
                    left: 0,
                    backgroundColor:WHITE,
                    minWidth:'90px !important',
                    fontWeight:'bold',
                  }}
                  >
                    {t("EDIT")}
                </TableCell>*/}
                <TableCell sx={{fontWeight:'bold', whiteSpace:'nowrap', color:GREEN_LOGO, textTransform:'none'}}>{t("Cognome")}</TableCell>
                <TableCell sx={{fontWeight:'bold', whiteSpace:'nowrap', color:GREEN_LOGO, textTransform:'none'}}>{t("Nome")}</TableCell>
                <TableCell sx={{fontWeight:'bold', whiteSpace:'nowrap', color:GREEN_LOGO, textTransform:'none'}}>{t("Email")}</TableCell>
                <TableCell sx={{fontWeight:'bold', whiteSpace:'nowrap', color:GREEN_LOGO, textTransform:'none'}}>{t("Status")}</TableCell>
                <TableCell sx={{fontWeight:'bold', whiteSpace:'nowrap', color:GREEN_LOGO, textTransform:'none'}}>{t("Abilitato")}</TableCell>
                <TableCell sx={{fontWeight:'bold', whiteSpace:'nowrap', color:GREEN_LOGO, textTransform:'none'}}>{t("Codice Fiscale")}</TableCell>
                <TableCell sx={{fontWeight:'bold', whiteSpace:'nowrap', color:GREEN_LOGO, textTransform:'none'}}>{t("Data di Nascita")}</TableCell>
                <TableCell sx={{fontWeight:'bold', whiteSpace:'nowrap', color:GREEN_LOGO, textTransform:'none'}}>{t("Indirizzo")}</TableCell>
                <TableCell sx={{fontWeight:'bold', whiteSpace:'nowrap', color:GREEN_LOGO, textTransform:'none'}}>{t("Comune di Nascita")}</TableCell>
                <TableCell sx={{fontWeight:'bold', whiteSpace:'nowrap', color:GREEN_LOGO, textTransform:'none'}}>{t("Stato di Nascita")}</TableCell>
                <TableCell sx={{fontWeight:'bold', whiteSpace:'nowrap', color:GREEN_LOGO, textTransform:'none'}}>{t("CAP")}</TableCell>
                <TableCell sx={{fontWeight:'bold', whiteSpace:'nowrap', color:GREEN_LOGO, textTransform:'none'}}>{t("Provincia")}</TableCell>
                <TableCell sx={{fontWeight:'bold', whiteSpace:'nowrap', color:GREEN_LOGO, textTransform:'none'}}>{t("Nazione")}</TableCell>
                <TableCell sx={{fontWeight:'bold', whiteSpace:'nowrap', color:GREEN_LOGO, textTransform:'none'}}>{t("Referral Code")}</TableCell>
                <TableCell sx={{fontWeight:'bold', whiteSpace:'nowrap', color:GREEN_LOGO, textTransform:'none'}}>{t("Data Eliminazione dalla Associazione")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user, index) => (
                <TableRow key={user.userId} sx={{
                  cursor:'pointer'
                }}>
                  {/*<TableCell sx={{
                      position: 'sticky',
                      borderRight:'1px solid ' + GRAY_LIGHT_RGB_BORDER,
                      left: 0,
                      backgroundColor: WHITE,
                      zIndex:1,
                      minWidth:'90px !important',
                    }}
                  >
                    {user.approvedFromAssociation === EApprovedFromAssociation.FirstRequest ?
                      <>
                      <Tooltip title={t("Approva Utente")}>
                          <IconButton onClick={() => {handleConfirmUser(user,ConfirmUser.Confirm)}}>
                            <CheckCircleIcon sx={{ color: 'green' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("Rifiuta Utente")}>
                          <IconButton onClick={() => {handleConfirmUser(user,ConfirmUser.Reject)}}>
                            <CancelIcon sx={{ color: 'red' }} />
                          </IconButton>
                        </Tooltip>
                    </> : user.approvedFromAssociation === EApprovedFromAssociation.RemovedFromAssociation ?
                    <Tooltip title={t("Approva Utente")}>
                        <IconButton onClick={() => {}}>
                          <CheckCircleIcon sx={{ color: 'green' }} />
                        </IconButton>
                      </Tooltip> :
                    <>
                        <Typography variant="h6" gutterBottom>
                          {t("Utente Approvato")}
                        </Typography>
                    </>
                    }
                  </TableCell>*/}
                  <TableCellCustom user={user} onClick={() => onUserSelect(user)}>{user.privateInfo?.cognome}</TableCellCustom>
                  <TableCellCustom user={user} sx={{whiteSpace:'nowrap'}} onClick={() => onUserSelect(user)}>{user.privateInfo?.nome}</TableCellCustom>
                  <TableCellCustom user={user} sx={{whiteSpace:'nowrap'}} onClick={() => onUserSelect(user)}>{user.email}</TableCellCustom>
                  <TableCellCustom user={user} sx={{whiteSpace:'nowrap'}} onClick={() => onUserSelect(user)}>{t(EApprovedFromAssociation[user.approvedFromAssociation ?? 0])}</TableCellCustom>
                  <TableCellCustom user={user} sx={{whiteSpace:'nowrap'}} onClick={() => onUserSelect(user)}>{user.enabledUser ? t('Sì') : t('No')}</TableCellCustom>
                  <TableCellCustom user={user} sx={{whiteSpace:'nowrap'}} onClick={() => onUserSelect(user)}>{user.privateInfo?.codiceFiscale}</TableCellCustom>
                  <TableCellCustom user={user} sx={{whiteSpace:'nowrap'}} onClick={() => onUserSelect(user)}>{user.privateInfo?.born ? new Date(user.privateInfo?.born).toLocaleDateString() : ''}</TableCellCustom>
                  <TableCellCustom user={user} sx={{whiteSpace:'nowrap'}} onClick={() => onUserSelect(user)}>{user.privateInfo?.address}</TableCellCustom>
                  <TableCellCustom user={user} sx={{whiteSpace:'nowrap'}} onClick={() => onUserSelect(user)}>{user.privateInfo?.comuneDiNascita}</TableCellCustom>
                  <TableCellCustom user={user} sx={{whiteSpace:'nowrap'}} onClick={() => onUserSelect(user)}>{user.privateInfo?.statoDiNascita}</TableCellCustom>
                  <TableCellCustom user={user} sx={{whiteSpace:'nowrap'}} onClick={() => onUserSelect(user)}>{user.privateInfo?.cap}</TableCellCustom>
                  <TableCellCustom user={user} sx={{whiteSpace:'nowrap'}} onClick={() => onUserSelect(user)}>{user.privateInfo?.provincia}</TableCellCustom>
                  <TableCellCustom user={user} sx={{whiteSpace:'nowrap'}} onClick={() => onUserSelect(user)}>{user.privateInfo?.nation}</TableCellCustom>
                  <TableCellCustom user={user} sx={{whiteSpace:'nowrap'}} onClick={() => onUserSelect(user)}>{user.refCode}</TableCellCustom>
                  <TableCellCustom user={user} sx={{whiteSpace:'nowrap'}} onClick={() => onUserSelect(user)}>
                    {user.removedFromAssociation &&
                    new Date(user.removedFromAssociation).getFullYear() !== 1
                      ? new Date(user.removedFromAssociation).toLocaleDateString()
                      : '-'}
                  </TableCellCustom>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CustomTableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelRowsPerPage={t("Risultati per pagina")}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("di")} ${count}`}
          sx={{
            color:GREEN_MAIN,
            "& .MuiTablePagination-select": {
                "&:focus": {
                  backgroundColor: "transparent",
                },
            },
            // This targets the dropdown arrow icon
            "& .MuiTablePagination-selectIcon": {
                color: GREEN_MAIN,
            },
            // This targets the navigation arrows
            "& .MuiTablePagination-actions": {
                "& .MuiIconButton-root": {
                  color: GREEN_MAIN,
                },
            },
            // Optional: Style the text to match
            "& .MuiTablePagination-displayedRows": {
                color: GREEN_MAIN,
            },
            "& .MuiTablePagination-selectLabel": {
                color: GREEN_MAIN,
            },
            // Add scrollbar styling
            "&::-webkit-scrollbar": {
              height: "8px",
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.mode === "light" ? "#f1f1f1" : "#2d2d2d",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.primary.main,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
            "&::-webkit-scrollbar-button": {
              display: "none !important",
              width: "0px",
              height: "0px",
            },
            scrollbarWidth: "thin",
            scrollbarColor: `${GREEN_LOGO} ${theme.palette.mode === "light" ? "#f1f1f1" : "#2d2d2d"}`,
            msOverflowStyle: "none",
            overflowX: "auto",
            maxWidth: "100%",
          }}
        />

        <Grid container spacing={2} justifyContent="left" paddingTop={2}>
          <Grid item xs='auto'>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 10, 
                height: 10, 
                backgroundColor: GREEN_MAIN 
              }} />
              <Typography sx={{ color: GREEN_MAIN, fontSize: '12px' }}>
                {t("Abilitato")}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs='auto'>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 10, 
                height: 10, 
                backgroundColor: `${GREEN_LOGO}75` 
              }} />
              <Typography sx={{ color: `${GREEN_LOGO}75`, fontSize: '12px' }}>
                {t("Disabilitato")}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs='auto'>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 10, 
                height: 10, 
                backgroundColor: `red` 
              }} />
              <Typography sx={{ color: `red`, fontSize: '12px' }}>
                {t("Richiesta di iscrizione")}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      

    </Box>
  );
};

export default AssociationPrivateUsersTable;
